import { type CSSProperties } from "react"
import { clientCompanies, type ClientCompany } from "@/data/clientele"
import { useReducedMotion } from "@/hooks/useReducedMotion"
import { cn } from "@/lib/utils"

const showcaseClients = clientCompanies
  .filter((client) => !client.logoSrc.startsWith("/images/clients/unverified/"))
  .sort((a, b) => a.canonicalName.localeCompare(b.canonicalName))

const buildRows = (rowCount: number) => {
  const rows = Array.from({ length: rowCount }, () => [] as ClientCompany[])
  showcaseClients.forEach((client, index) => {
    rows[index % rowCount].push(client)
  })
  return rows.filter((row) => row.length > 0)
}

const mobileCarouselRows = buildRows(6)
const desktopCarouselRows = buildRows(3)

type CarouselLogoProps = {
  client: ClientCompany
}

const invertOnHoverLogoIds = new Set([
  "garware-hitech-films",
  "lupin-limited",
  "ab-inbev-india",
])

function CarouselLogo({ client }: CarouselLogoProps) {
  const item = (
    <div className={cn("client-carousel-item", client.logoSurface === "dark" && "client-carousel-item--dark")}>
      <div className="client-carousel-logo-frame">
        <img
          src={client.logoSrc}
          alt={`${client.canonicalName} logo`}
          loading="lazy"
          decoding="async"
          className={cn(
            "client-carousel-logo",
            invertOnHoverLogoIds.has(client.id) && "client-carousel-logo--invert",
          )}
        />
      </div>
    </div>
  )

  if (!client.website) {
    return item
  }

  return (
    <a
      href={client.website}
      target="_blank"
      rel="noopener noreferrer"
      className="block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-300/80 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-50"
    >
      {item}
    </a>
  )
}

type CarouselRowProps = {
  row: ClientCompany[]
  reverse?: boolean
  duration: number
}

function CarouselRow({ row, reverse, duration }: CarouselRowProps) {
  const prefersReducedMotion = useReducedMotion()

  if (prefersReducedMotion) {
    return (
      <div className="client-carousel-static-row">
        {row.map((client) => (
          <CarouselLogo key={client.id} client={client} />
        ))}
      </div>
    )
  }

  const repeated = [...row, ...row]
  const style = {
    animationDuration: `${duration}s`,
  } as CSSProperties

  return (
    <div className="client-carousel-mask">
      <div
        className={cn("client-carousel-track", reverse && "client-carousel-track--reverse")}
        style={style}
      >
        {repeated.map((client, index) => (
          <CarouselLogo key={`${client.id}-${index}`} client={client} />
        ))}
      </div>
    </div>
  )
}

export function ClienteleSection() {
  return (
    <section className="section-padding bg-white client-carousel-section">
      <div className="container mx-auto px-4 relative z-10">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 tracking-tight">
            Trusted by <span className="text-gradient-red">Industry Clients</span>
          </h2>
          <p className="text-slate-600 leading-relaxed">
            Reliable partners across complex operations where safety, uptime, and execution quality
            matter most.
          </p>
        </div>

        <div className="client-carousel-stack client-carousel-stack--mobile mt-10">
          {mobileCarouselRows.map((row, index) => (
            <CarouselRow
              key={`client-row-mobile-${index}`}
              row={row}
              reverse={index % 2 === 1}
              duration={index % 2 === 0 ? 30 : 36}
            />
          ))}
        </div>

        <div className="client-carousel-stack client-carousel-stack--desktop mt-10">
          {desktopCarouselRows.map((row, index) => (
            <CarouselRow
              key={`client-row-desktop-${index}`}
              row={row}
              reverse={index % 2 === 1}
              duration={index % 2 === 0 ? 34 : 40}
            />
          ))}
        </div>
      </div>
    </section>
  )
}
